import { AxiosError } from 'axios';
import { apiClient, apiGet } from './client';
import type { OperationResult } from './types';
import type { NavScope } from '@/layouts/navigation';

// Agent Studio module catalog — vNEXT RestApi.Tenant AgentStudioModulesController (api/AgentStudioModules).
// Placement and presentation only: the server never sends a route to execute or a permission to grant;
// it files keys from the shared destination manifest under modules. Versions are base64 rowversions.

// ── Navigation (every Studio user) ───────────────────────────────────────────

export interface EffectiveDestination {
  navKey: string;
  title: string;
  route: string;
  scope: NavScope;
}

export interface EffectiveModule {
  key: string;
  title: string;
  description?: string | null;
  iconKey: string;
  color?: string | null;
  /** Already resolved for this user: the stored default when they may open it, else their first destination. */
  defaultNavKey: string;
  items: EffectiveDestination[];
}

export interface EffectiveElsewhere extends EffectiveDestination {
  moduleKey?: string | null;
  moduleTitle?: string | null;
  reason: 'hidden-module' | 'empty-module' | 'unplaced';
}

export interface EffectiveNavigation {
  catalogRevision: string;
  manifestVersion: number;
  manifestKeys: string[];
  canManageModules: boolean;
  home: EffectiveDestination;
  modules: EffectiveModule[];
  elsewhere: EffectiveElsewhere[];
}

// ── Catalog management (admins) ──────────────────────────────────────────────

export interface StudioModuleItem {
  id: number;
  navKey: string;
  displayOrder: number;
  rowVersion?: string | null;
  known: boolean;
  title: string;
  route?: string | null;
  scope?: NavScope | null;
}

export interface StudioModule {
  id: number;
  moduleKey: string;
  title: string;
  description?: string | null;
  iconKey: string;
  color?: string | null;
  displayOrder: number;
  defaultNavKey?: string | null;
  isVisible: boolean;
  isSystem: boolean;
  isDraft: boolean;
  rowVersion?: string | null;
  lastUpdatedUtc?: string | null;
  lastUpdatedBy?: number | null;
  items: StudioModuleItem[];
}

export interface StudioDestination {
  navKey: string;
  title: string;
  route: string;
  scope: NavScope;
  access: 'studio' | 'admin' | 'claim';
  claim?: string | null;
  ownerModuleId?: number | null;
  ownerModuleKey?: string | null;
  itemRowVersion?: string | null;
}

export interface StudioCatalogDiagnostics {
  unknownNavKeys: string[];
  unplacedNavKeys: string[];
  invalidDefaults: string[];
  recoveryRouteHidden: boolean;
}

export interface StudioModuleCatalog {
  catalogRevision: string;
  manifestVersion: number;
  modules: StudioModule[];
  destinations: StudioDestination[];
  iconKeys: string[];
  diagnostics: StudioCatalogDiagnostics;
}

export interface StudioModuleChange {
  id: number;
  moduleId: number;
  moduleKey: string;
  action: 'seeded' | 'created' | 'updated' | 'reordered' | 'deleted' | 'moved-out';
  beforeJson?: string | null;
  afterJson?: string | null;
  actorId: number;
  actorName?: string | null;
  atUtc: string;
}

export interface SaveStudioModule {
  moduleKey?: string;
  title: string;
  description?: string | null;
  iconKey: string;
  color?: string | null;
  displayOrder?: number | null;
  defaultNavKey?: string | null;
  isVisible: boolean;
  rowVersion?: string | null;
  items: Array<{ navKey: string; rowVersion?: string | null }>;
}

export interface StudioModuleProblem {
  field?: string | null;
  code: string;
  message: string;
}

/** What a write came back with. `conflict` carries the CURRENT module when the server had one to show. */
export type StudioModuleWrite =
  | { kind: 'saved'; module: StudioModule | null }
  | { kind: 'invalid'; problems: StudioModuleProblem[] }
  | { kind: 'conflict'; message: string; current: StudioModule | null }
  | { kind: 'forbidden'; message: string }
  | { kind: 'missing'; message: string };

interface WriteBody {
  module?: StudioModule | null;
  problems?: StudioModuleProblem[];
  outcome?: string;
}

/** A refused body after the client's camelCase pass: the OperationResult envelope, or ASP.NET's ProblemDetails for a 400. */
type RefusedBody = Partial<Omit<OperationResult<WriteBody>, 'errors'>> & { title?: string; detail?: string; errors?: Record<string, string[]> };

const BASE = '/api/AgentStudioModules';

/** A failed read, with the HTTP status when there was one (403 = not an admin). */
export class StudioModulesApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'StudioModulesApiError';
  }
}

/** Unwraps OperationResult and turns an unsuccessful envelope into an error carrying the server's words. */
async function read<T>(url: string, params?: unknown): Promise<T> {
  let res: OperationResult<T>;
  try {
    res = await apiGet<OperationResult<T>>(url, params);
  } catch (err) {
    throw new StudioModulesApiError(describe(err), (err as AxiosError)?.response?.status);
  }
  if (!res.isSuccess) throw new StudioModulesApiError(res.message || 'The server could not answer.');
  return res.data as T;
}

export const getEffectiveNavigation = (): Promise<EffectiveNavigation> => read<EffectiveNavigation>(`${BASE}/Effective`);
export const getModuleCatalog = (): Promise<StudioModuleCatalog> => read<StudioModuleCatalog>(BASE);
export const getModuleHistory = (moduleId?: number, take = 100): Promise<StudioModuleChange[]> =>
  read<StudioModuleChange[]>(`${BASE}/History`, { moduleId, take });

export const createModule = (body: SaveStudioModule) => write(() => apiClient.post(BASE, body));
export const updateModule = (id: number, body: SaveStudioModule) => write(() => apiClient.put(`${BASE}/${id}`, body));
export const reorderModules = (modules: Array<{ id: number; rowVersion?: string | null }>) =>
  write(() => apiClient.post(`${BASE}/Reorder`, { modules }));
export const deleteModule = (id: number, rowVersion?: string | null) =>
  write(() => apiClient.delete(`${BASE}/${id}`, { params: { rowVersion } }));

async function write(send: () => Promise<{ data: OperationResult<WriteBody> }>): Promise<StudioModuleWrite> {
  try {
    const { data } = await send();
    if (data.isSuccess) return { kind: 'saved', module: data.data?.module ?? null };
    return { kind: 'invalid', problems: data.data?.problems ?? [{ code: 'error', message: data.message || 'The server refused the change.' }] };
  } catch (err) {
    const e = err as AxiosError<RefusedBody>;
    const body = typeof e.response?.data === 'object' ? e.response.data : undefined;
    const message = describe(err);
    switch (e.response?.status) {
      case 409: return { kind: 'conflict', message, current: body?.data?.module ?? null };
      case 403: return { kind: 'forbidden', message };
      case 404: return { kind: 'missing', message };
      case 400: return { kind: 'invalid', problems: problemsOf(body, message) };
      default: throw new Error(message);
    }
  }
}

/** A 400's field problems: the manager's own list, else model-binding errors per field, else the message alone. */
function problemsOf(body: RefusedBody | undefined, message: string): StudioModuleProblem[] {
  if (body?.data?.problems?.length) return body.data.problems;
  const fromBinding = Object.entries(body?.errors ?? {}).flatMap(([field, messages]) =>
    (messages ?? []).map((m) => ({ field: field.replace(/^\$\.?/, '') || null, code: 'invalid', message: m })),
  );
  return fromBinding.length ? fromBinding : [{ code: 'invalid', message }];
}

function describe(err: unknown): string {
  const e = err as AxiosError<RefusedBody>;
  const body = e?.response?.data && typeof e.response.data === 'object' ? e.response.data : undefined;
  if (e?.response?.status === 403) return body?.message || 'Only an Agent Studio admin can manage modules.';
  if (body?.message) return body.message;
  if (body?.detail || body?.title) return body.detail || body.title!;
  if (e?.response?.status) return `The server answered ${e.response.status}.`;
  return (err as Error)?.message || 'Could not reach the server.';
}
